import { useEffect } from 'react';
import { Box, Button, Card, CardContent, Container, Grid, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { PageHead } from '../../components/public/PageHead';
import { CANTIR_FB_PIXEL_ID, trackCantirLandingFacebookPageView } from '../../utils/facebookPixelLanding';

const valueCards = [
  { title: 'Gestione commesse', text: 'Organizza cantieri, clienti e attività in un unico posto.' },
  { title: 'Rapportini digitali', text: 'Compila e firma i rapportini da campo, genera PDF.' },
  { title: 'Foto, note e audio', text: 'Registra ciò che succede in cantiere con timeline e allegati.' },
  { title: 'Materiali e DDT', text: 'Catalogo materiali, bolle e consumi tracciati per commessa.' },
  { title: 'Documenti e condivisione', text: 'Preventivi, fatture, documenti condivisi con i clienti.' },
  { title: 'Controllo operativo', text: 'Presenze, ore, mezzi e costi sotto controllo.' },
];

const steps = ['Crea la commessa', 'Registra attività e materiali', 'Genera rapportini e documenti', 'Tieni sotto controllo il lavoro'];

const targets = ['Impiantisti', 'Manutentori', 'Elettricisti', 'Termoidraulici', 'Serramentisti', 'Imprese specializzate'];

const highlights = ['Commesse e clienti', 'Timeline operativa', 'Milestone', 'Rapportini e firma', 'Documenti PDF', 'Presenze e ore', 'Materiali e catalogo', 'Bolle / DDT', 'Mezzi e consumi'];

export function LandingPage() {
  useEffect(() => {
    trackCantirLandingFacebookPageView();
  }, []);

  return (
    <>
      <noscript>
        <img
          height={1}
          width={1}
          style={{ display: 'none' }}
          src={`https://www.facebook.com/tr?id=${CANTIR_FB_PIXEL_ID}&ev=PageView&noscript=1`}
          alt=""
        />
      </noscript>
      <PageHead title="Gestione cantieri e commesse" description="Cantir: gestione cantieri, commesse, rapportini digitali, materiali e DDT. Soluzione operativa per imprese di impianti, manutenzione e costruzione." />
      <Box sx={{ py: { xs: 6, md: 10 }, px: 2, backgroundColor: 'primary.main', color: 'primary.contrastText' }}>
        <Container maxWidth="md">
          <Typography component="h1" variant="h3" fontWeight={700} gutterBottom align="center">Gestione cantieri e commesse, semplice e completa</Typography>
          <Typography variant="h6" align="center" sx={{ opacity: 0.95, mb: 3 }}>Rapportini digitali, materiali, DDT e documenti: tutto ciò che serve per tenere sotto controllo il lavoro in cantiere.</Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button component={RouterLink} to="/contact" variant="contained" size="large" sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'grey.100' } }}>Richiedi demo</Button>
            <Button component={RouterLink} to="/login" variant="outlined" size="large" sx={{ borderColor: 'white', color: 'white', '&:hover': { borderColor: 'grey.300', bgcolor: 'rgba(255,255,255,0.08)' } }}>Accedi</Button>
          </Box>
        </Container>
      </Box>
      <Box sx={{ py: 8, px: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={700} align="center" gutterBottom>Perché Cantir</Typography>
          <Typography color="text.secondary" align="center" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>Tutto cio che serve per gestire commesse, squadra e documentazione in cantiere.</Typography>
          <Grid container spacing={3}>
            {valueCards.map((card) => (
              <Grid item xs={12} sm={6} md={4} key={card.title}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>{card.title}</Typography>
                    <Typography color="text.secondary">{card.text}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      <Box sx={{ py: 8, px: 2, backgroundColor: 'grey.50' }}>
        <Container maxWidth="md">
          <Typography variant="h4" fontWeight={700} align="center" gutterBottom>Come funziona</Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', gap: 3, mt: 4 }}>
            {steps.map((step, i) => (
              <Box key={step} sx={{ textAlign: 'center', flex: 1 }}>
                <Typography variant="h4" color="primary" fontWeight={700}>{i + 1}</Typography>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>{step}</Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>
      <Box sx={{ py: 8, px: 2 }}>
        <Container maxWidth="md">
          <Typography variant="h4" fontWeight={700} align="center" gutterBottom>Pensato per</Typography>
          <Typography color="text.secondary" align="center" sx={{ mb: 3 }}>Imprese e professionisti che gestiscono cantieri e commesse.</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
            {targets.map((t) => (
              <Typography key={t} component="span" sx={{ px: 2, py: 1, bgcolor: 'action.hover', borderRadius: 2 }}>{t}</Typography>
            ))}
          </Box>
        </Container>
      </Box>
      <Box sx={{ py: 8, px: 2, backgroundColor: 'grey.50' }}>
        <Container maxWidth="md">
          <Typography variant="h4" fontWeight={700} align="center" gutterBottom>Funzionalità</Typography>
          <Typography color="text.secondary" align="center" sx={{ mb: 4 }}>Commesse, timeline, rapportini, documenti, presenze, materiali, bolle e mezzi.</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
            {highlights.map((h) => (
              <Typography key={h} component="span" variant="body2" sx={{ px: 2, py: 1, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1 }}>{h}</Typography>
            ))}
          </Box>
        </Container>
      </Box>
      <Box sx={{ py: 8, px: 2 }}>
        <Container maxWidth="sm">
          <Typography variant="h4" fontWeight={700} align="center" gutterBottom>Richiedi una demo</Typography>
          <Typography color="text.secondary" align="center" sx={{ mb: 3 }}>Scopri come Cantir può semplificare la gestione dei tuoi cantieri.</Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button component={RouterLink} to="/contact" variant="contained" size="large">Contattaci</Button>
            <Button component={RouterLink} to="/login" variant="outlined" size="large">Accedi</Button>
          </Box>
        </Container>
      </Box>
    </>
  );
}
